import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Trophy, Calendar, LogIn, LogOut, Crown, TrendingUp, TrendingDown, Minus, Flame, LineChart, Megaphone, Search, Edit, Send, Save, Check, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// SC Brühl Logo
const SC_BRUEHL_LOGO = 'https://scbruehl.ch/wp-content/themes/scbruehl/assets/img/scb_logo.svg';

// Background image for main content - SC Brühl Stadium
const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_bruehltipp/artifacts/jzqv7iif_IMG_0315.jpeg';

// Helper for change indicators
const ChangeIndicator = ({ value, onClick, clickable }) => {
    if (value === null || value === undefined) return <span className="text-gray-400 text-sm">-</span>;
    if (value === 0) return (
        <span 
            className={`text-gray-400 text-sm flex items-center justify-center ${clickable ? 'cursor-pointer hover:text-gray-200' : ''}`}
            onClick={onClick}
        >
            <Minus className="w-3 h-3" />
        </span>
    );
    const isPositive = value > 0;
    return (
        <span 
            className={`flex items-center justify-center text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'} ${clickable ? 'cursor-pointer hover:opacity-80' : ''}`}
            onClick={onClick}
        >
            {isPositive ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
            {isPositive ? '+' : ''}{value}
        </span>
    );
};

const statusColors = {
    'offen': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    'geschlossen': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    'ausgewertet': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
};

const statusLabels = {
    'offen': 'Offen',
    'geschlossen': 'Geschlossen',
    'ausgewertet': 'Ausgewertet'
};

// Helper function to render markdown links [text](url)
const renderMarkdownLinks = (text) => {
    if (!text) return null;
    
    // Regex to match markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;
    
    while ((match = linkRegex.exec(text)) !== null) {
        // Add text before the link
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        
        // Add the link
        const linkText = match[1];
        const url = match[2];
        parts.push(
            <a 
                key={`link-${keyIndex++}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00ad43] hover:text-[#00c94e] underline"
            >
                {linkText}
            </a>
        );
        
        lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after the last link
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
};

// Convert UTC time to local time based on timezone  
const convertFromUTC = (utcTime, timezone) => {
    if (!utcTime) return '';
    const [hours, minutes] = utcTime.split(':').map(Number);
    
    // Create a UTC date
    const today = new Date();
    const utcDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes));
    
    // Convert to local timezone
    const localStr = utcDate.toLocaleTimeString('de-CH', { 
        timeZone: timezone || 'Europe/Zurich', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
    });
    
    return localStr;
};

export default function HomePage() {
    const { user, logout, api } = useAuth();
    const navigate = useNavigate();
    const [lastMatch, setLastMatch] = useState(null);
    const [nextMatch, setNextMatch] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [announcement, setAnnouncement] = useState('');
    const [loading, setLoading] = useState(true);
    const [rankHistoryModal, setRankHistoryModal] = useState({ open: false, userId: null, userName: '' });
    const [rankHistory, setRankHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Public tip registration
    const [showTipDialog, setShowTipDialog] = useState(false);
    const [allMatches, setAllMatches] = useState([]);
    const [tipForm, setTipForm] = useState({
        name_firma: '',
        email: '',
        address: '',
        supported_player: '',
        amount_per_goal: ''
    });
    const [tipPredictions, setTipPredictions] = useState({});
    const [submittingTips, setSubmittingTips] = useState(false);

    // Authenticated user tipping
    const [showUserTipDialog, setShowUserTipDialog] = useState(false);
    const [userMatches, setUserMatches] = useState([]);
    const [userPredictions, setUserPredictions] = useState({});
    const [userTipInputs, setUserTipInputs] = useState({});
    const [savingUserTips, setSavingUserTips] = useState(false);
    const [loadingUserMatches, setLoadingUserMatches] = useState(false);

    // Profile popup
    const [showProfilePopup, setShowProfilePopup] = useState(false);
    const [editPseudonym, setEditPseudonym] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [matchesRes, leaderboardRes, announcementRes] = await Promise.all([
                    axios.get(`${API_URL}/matches/public`),
                    axios.get(`${API_URL}/leaderboard/public`),
                    axios.get(`${API_URL}/announcement`)
                ]);
                setLastMatch(matchesRes.data.last_match);
                setNextMatch(matchesRes.data.next_match);
                setLeaderboard(leaderboardRes.data);
                setAnnouncement(announcementRes.data.text || '');
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const openTipDialog = async () => {
        setShowTipDialog(true);
        try {
            const res = await axios.get(`${API_URL}/matches`);
            // Filter only open matches
            const openMatches = res.data.filter(m => m.status === 'offen').sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
            setAllMatches(openMatches);
        } catch (error) {
            console.error('Failed to fetch matches:', error);
        }
    };

    const handleTipPredictionChange = (matchId, field, value) => {
        setTipPredictions(prev => ({
            ...prev,
            [matchId]: {
                ...prev[matchId],
                [field]: value
            }
        }));
    };

    const handleSubmitTips = async () => {
        // Validate required fields
        if (!tipForm.name_firma.trim()) {
            toast.error('Bitte Name / Firma eingeben');
            return;
        }
        if (!tipForm.email.trim()) {
            toast.error('Bitte Email eingeben');
            return;
        }
        if (!tipForm.address.trim()) {
            toast.error('Bitte Adresse eingeben');
            return;
        }
        const amount = parseFloat(tipForm.amount_per_goal);
        if (isNaN(amount) || amount < 2) {
            toast.error('Bitte Betrag pro Tor eingeben (Minimum CHF 2)');
            return;
        }

        // Collect predictions
        const predictions = [];
        for (const [matchId, scores] of Object.entries(tipPredictions)) {
            if (scores.home !== '' && scores.away !== '' && scores.home !== undefined && scores.away !== undefined) {
                predictions.push({
                    match_id: matchId,
                    home_score: parseInt(scores.home),
                    away_score: parseInt(scores.away)
                });
            }
        }

        if (predictions.length === 0) {
            toast.error('Bitte mindestens einen Tipp eingeben');
            return;
        }

        setSubmittingTips(true);
        try {
            await axios.post(`${API_URL}/public/register-tips`, {
                name_firma: tipForm.name_firma.trim(),
                email: tipForm.email.trim().toLowerCase(),
                address: tipForm.address.trim(),
                supported_player: tipForm.supported_player.trim() || null,
                amount_per_goal: amount,
                predictions: predictions
            });
            
            toast.success('Anmeldung erfolgreich! Vielen Dank für Ihre Teilnahme.');
            setShowTipDialog(false);
            // Reset form
            setTipForm({ name_firma: '', email: '', address: '', supported_player: '', amount_per_goal: '' });
            setTipPredictions({});
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler bei der Anmeldung');
        } finally {
            setSubmittingTips(false);
        }
    };

    const openRankHistory = async (userId, userName) => {
        setRankHistoryModal({ open: true, userId, userName });
        setLoadingHistory(true);
        try {
            const res = await axios.get(`${API_URL}/rank-history/${userId}`);
            setRankHistory(res.data);
        } catch (error) {
            console.error('Failed to fetch rank history:', error);
            setRankHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Authenticated user: open tipping dialog
    const openUserTipDialog = async () => {
        setShowUserTipDialog(true);
        setLoadingUserMatches(true);
        try {
            const [matchesRes, predsRes] = await Promise.all([
                api().get('/matches'),
                api().get('/predictions')
            ]);
            const openMatches = matchesRes.data
                .filter(m => m.status === 'offen')
                .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
            setUserMatches(openMatches);

            // Map existing predictions
            const predMap = {};
            predsRes.data.forEach(p => { predMap[p.match_id] = p; });
            setUserPredictions(predMap);

            // Pre-fill inputs from existing predictions
            const inputs = {};
            openMatches.forEach(m => {
                const existing = predMap[m.id];
                if (existing) {
                    inputs[m.id] = { home: String(existing.home_score), away: String(existing.away_score) };
                }
            });
            setUserTipInputs(inputs);
        } catch (error) {
            console.error('Failed to fetch matches:', error);
        } finally {
            setLoadingUserMatches(false);
        }
    };

    const handleUserTipChange = (matchId, field, value) => {
        setUserTipInputs(prev => ({
            ...prev,
            [matchId]: { ...prev[matchId], [field]: value }
        }));
    };

    const handleSaveUserTips = async () => {
        setSavingUserTips(true);

        // Collect all valid tips for batch save
        const batchPredictions = [];
        for (const match of userMatches) {
            const input = userTipInputs[match.id];
            if (!input || input.home === '' || input.away === '' || input.home === undefined || input.away === undefined) continue;

            const homeScore = parseInt(input.home);
            const awayScore = parseInt(input.away);
            if (isNaN(homeScore) || isNaN(awayScore)) continue;

            const existing = userPredictions[match.id];
            if (existing && existing.home_score === homeScore && existing.away_score === awayScore) continue;

            batchPredictions.push({
                match_id: match.id,
                home_score: homeScore,
                away_score: awayScore
            });
        }

        if (batchPredictions.length === 0) {
            setSavingUserTips(false);
            setShowUserTipDialog(false);
            return;
        }

        try {
            const res = await api().post('/predictions/batch', { predictions: batchPredictions });
            const saved = res.data.saved || 0;
            const errors = res.data.errors || [];
            if (saved > 0) toast.success(`${saved} Tipp(s) gespeichert!`);
            if (errors.length > 0) toast.error(`${errors.length} Tipp(s) konnten nicht gespeichert werden`);
            setShowUserTipDialog(false);
        } catch (error) {
            console.error('Error saving predictions:', error);
            toast.error('Fehler beim Speichern der Tipps');
        } finally {
            setSavingUserTips(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const openProfilePopup = () => {
        setEditPseudonym(user?.pseudonym || '');
        setShowProfilePopup(true);
    };

    const handleSaveProfile = async () => {
        if (!editPseudonym.trim()) {
            toast.error('Pseudonym darf nicht leer sein');
            return;
        }
        setSavingProfile(true);
        try {
            const res = await api().put('/auth/profile', { pseudonym: editPseudonym.trim() });
            // Update user in auth context
            const updatedUser = { ...user, pseudonym: res.data.pseudonym };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSavingProfile(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    const MatchCard = ({ match, label, icon: Icon, isNext }) => {
        if (!match) {
            return (
                <Card className="card-hover border border-dashed border-gray-600 shadow-lg bg-black/90 text-white">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-gray-300 mb-2">
                            <Icon className="w-4 h-4" />
                            <span className="font-medium text-sm">{label}</span>
                        </div>
                        <p className="text-gray-400 text-center text-sm py-2">Kein Spiel vorhanden</p>
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card className={`card-hover overflow-hidden shadow-lg bg-black/90 text-white border-0 ${isNext ? 'ring-2 ring-[#00ad43] ring-offset-2 ring-offset-transparent' : ''}`}>
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-[#00ad43]" />
                            <span className="font-medium text-sm text-white">{label}</span>
                            {isNext && <Flame className="w-3 h-3 text-orange-500" />}
                        </div>
                        <Badge className={`text-xs ${statusColors[match.status]}`}>
                            {statusLabels[match.status]}
                        </Badge>
                    </div>
                    <p className="text-xs text-gray-400 text-center mb-2">
                        {format(parseISO(match.match_date), 'dd.MM.yyyy', { locale: de })}
                        {match.match_time && ` | ${convertFromUTC(match.match_time, match.match_timezone)} Uhr`}
                    </p>
                    <div className="flex items-center justify-center gap-3 text-white">
                        <span className="team-name text-sm flex-1 text-right">{match.home_team}</span>
                        <span className="text-lg font-heading tabular-nums px-2 text-[#00ad43]">
                            {match.status === 'ausgewertet' 
                                ? `${match.home_score} : ${match.away_score}`
                                : 'vs'
                            }
                        </span>
                        <span className="team-name text-sm flex-1 text-left">{match.away_team}</span>
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="hero-background py-6 px-4">
                <div className="container mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img 
                            src={SC_BRUEHL_LOGO} 
                            alt="SC Brühl" 
                            className="w-14 h-14"
                        />
                        <div>
                            <h1 className="text-2xl font-heading tracking-tight text-white">Kronentipp</h1>
                            <p className="text-green-200 text-sm">SC Brühl St. Gallen</p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {user ? (
                            <>
                                <button 
                                    onClick={openProfilePopup}
                                    className="flex items-center gap-2 text-white text-sm font-medium hover:text-green-200 transition-colors cursor-pointer"
                                    data-testid="home-user-name"
                                >
                                    <User className="w-4 h-4" />
                                    {user.pseudonym || `${user.first_name} ${user.last_name}`}
                                </button>
                                <Button 
                                    variant="outline" 
                                    className="bg-[#00ad43]/80 border-[#00ad43] text-white hover:bg-[#00ad43] gap-2"
                                    onClick={openUserTipDialog}
                                    data-testid="home-tip-button"
                                >
                                    <Edit className="w-4 h-4" />
                                    Spiele tippen
                                </Button>
                            </>
                        ) : (
                            <Link to="/login">
                                <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 gap-2" data-testid="home-login-button">
                                    <LogIn className="w-4 h-4" />
                                    Anmelden
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content with Background Image */}
            <main className="relative min-h-[calc(100vh-80px)]">
                {/* Background Image Layer */}
                <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${BG_IMAGE})` }}
                >
                    {/* Overlay for transparency - reduced opacity for more visible background */}
                    <div className="absolute inset-0 bg-white/70" />
                </div>
                
                {/* Content Layer */}
                <div className="relative container mx-auto px-4 py-8 space-y-6">
                    {/* Announcement Card - only shown if text exists */}
                    {announcement && (
                        <section>
                            <Card className="bg-black/90 border-0 shadow-lg">
                                <CardContent className="p-4 flex items-start gap-3">
                                    <Megaphone className="w-5 h-5 text-[#00ad43] flex-shrink-0 mt-0.5" />
                                    <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                                        {renderMarkdownLinks(announcement)}
                                    </p>
                                </CardContent>
                            </Card>
                        </section>
                    )}

                    {/* Matches Section */}
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <MatchCard 
                            match={lastMatch} 
                            label="Letztes Spiel" 
                            icon={Trophy}
                            isNext={false}
                        />
                        <MatchCard 
                            match={nextMatch} 
                            label="Nächstes Spiel" 
                            icon={Calendar}
                            isNext={nextMatch?.status === 'offen'}
                        />
                    </section>

                    {/* Leaderboard Section */}
                    <section>
                        <Card data-testid="public-leaderboard" className="overflow-hidden shadow-lg border-0 bg-black/90 text-white">
                            <CardHeader className="bg-[#00ad43]/20 border-b border-gray-700 py-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2 text-base text-white">
                                        <Crown className="w-4 h-4 text-[#00ad43]" />
                                        Rangliste
                                        {leaderboard.length > 0 && (
                                            <span className="text-xs font-normal text-gray-400 ml-2">
                                                {leaderboard.length} Teilnehmer
                                            </span>
                                        )}
                                    </CardTitle>
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                        <Input
                                            type="text"
                                            placeholder="Suchen..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="h-7 w-32 pl-7 text-xs bg-black/50 border-gray-600 text-white placeholder:text-gray-500"
                                            data-testid="leaderboard-search"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                        <CardContent className="p-0">
                            {leaderboard.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-black/50 border-gray-700">
                                            <TableHead className="w-14 text-xs text-gray-300 text-center">Rang</TableHead>
                                            <TableHead className="w-14 text-xs text-center text-gray-300">+/-</TableHead>
                                            <TableHead className="text-xs text-gray-300 pl-4">Teilnehmer</TableHead>
                                            <TableHead className="w-14 text-center text-xs text-gray-300">Punkte</TableHead>
                                            <TableHead className="w-14 text-xs text-center text-gray-300">+/-</TableHead>
                                            <TableHead className="w-32 text-center text-xs text-gray-300">Trend</TableHead>
                                            <TableHead className="w-24 text-center text-xs text-gray-300">Letzter Tipp</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {leaderboard
                                            .filter(entry => 
                                                searchQuery === '' || 
                                                entry.rank <= 3 ||
                                                entry.pseudonym.toLowerCase().includes(searchQuery.toLowerCase())
                                            )
                                            .map((entry) => (
                                            <TableRow 
                                                key={entry.user_id}
                                                className={`
                                                    border-gray-700
                                                    ${entry.rank === 1 ? 'bg-[#00ad43]/20' : ''}
                                                    ${entry.rank === 2 ? 'bg-[#00ad43]/10' : ''}
                                                    ${entry.rank === 3 ? 'bg-[#00ad43]/5' : ''}
                                                    hover:bg-gray-800 transition-colors
                                                `}
                                                data-testid={`public-leaderboard-row-${entry.rank}`}
                                            >
                                                <TableCell className="py-2 text-center">
                                                    <span className={`font-heading text-sm inline-flex items-center justify-center w-6 h-6 rounded-full ${
                                                        entry.rank === 1 ? 'bg-[#00ad43] text-white' :
                                                        entry.rank === 2 ? 'bg-[#00ad43]/70 text-white' :
                                                        entry.rank === 3 ? 'bg-[#00ad43]/50 text-white' : 'text-muted-foreground'
                                                    }`}>
                                                        {entry.rank}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="py-2 text-center text-sm text-white">
                                                    <ChangeIndicator 
                                                        value={entry.rank_change} 
                                                        clickable={true}
                                                        onClick={() => openRankHistory(entry.user_id, entry.pseudonym)}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium text-sm py-2 text-white pl-4">
                                                    {entry.pseudonym}
                                                    {entry.rank === 1 && <Crown className="w-3 h-3 inline ml-1 text-[#00ad43]" />}
                                                </TableCell>
                                                <TableCell className="text-center py-2">
                                                    <span className="font-medium text-sm tabular-nums text-white">{entry.total_points}</span>
                                                </TableCell>
                                                <TableCell className="py-2 text-center text-sm text-white">
                                                    <ChangeIndicator value={entry.points_change} />
                                                </TableCell>
                                                <TableCell className="text-center py-2">
                                                    <div className="flex items-center justify-center gap-0.5">
                                                        {entry.points_trend && entry.points_trend.length > 0 ? (
                                                            [...entry.points_trend].reverse().map((pts, idx) => (
                                                                <span 
                                                                    key={idx} 
                                                                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium ${
                                                                        pts >= 8 ? 'bg-[#00ad43] text-white' :
                                                                        pts >= 5 ? 'bg-[#00ad43]/70 text-white' :
                                                                        pts > 0 ? 'bg-[#00ad43]/40 text-white' :
                                                                        'bg-gray-600 text-gray-300'
                                                                    }`}
                                                                >
                                                                    {pts}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-gray-500 text-xs">-</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-center text-sm py-2 text-white font-medium">
                                                    {entry.last_prediction || '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <p className="text-gray-400 text-center py-6 text-sm">Noch keine Teilnehmer</p>
                            )}
                        </CardContent>
                    </Card>
                </section>

                {/* Points & Prizes Section - Side by Side */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Points Info */}
                    <Card className="bg-black/90 shadow-lg border-0 text-white">
                        <CardHeader className="bg-[#00ad43]/20 border-b border-gray-700 py-3">
                            <CardTitle className="flex items-center gap-2 text-base text-white">
                                <Trophy className="w-4 h-4 text-[#00ad43]" />
                                Punkteverteilung und Auswertung
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3">
                            <div className="grid grid-cols-4 gap-2 text-center mb-3">
                                <div className="bg-[#00ad43]/20 rounded-lg p-2 border border-[#00ad43]/30">
                                    <span className="text-xl font-heading text-[#00ad43]">5</span>
                                    <p className="text-sm text-white">Sieger</p>
                                </div>
                                <div className="bg-[#00ad43]/20 rounded-lg p-2 border border-[#00ad43]/30">
                                    <span className="text-xl font-heading text-[#00ad43]">1</span>
                                    <p className="text-sm text-white">Heim</p>
                                </div>
                                <div className="bg-[#00ad43]/20 rounded-lg p-2 border border-[#00ad43]/30">
                                    <span className="text-xl font-heading text-[#00ad43]">1</span>
                                    <p className="text-sm text-white">Gast</p>
                                </div>
                                <div className="bg-[#00ad43]/20 rounded-lg p-2 border border-[#00ad43]/30">
                                    <span className="text-xl font-heading text-[#00ad43]">3</span>
                                    <p className="text-sm text-white">Differenz</p>
                                </div>
                            </div>
                            <p className="text-sm text-gray-300 leading-relaxed mb-2">
                                5 Punkte bei korrektem Siegertipp. Je 1 Punkt für korrekte Heim- und Gast-Tore. 3 Punkte für korrekte Tordifferenz (nur bei richtigem Sieger). Max: <span className="text-[#00ad43] font-medium">10 Punkte</span>.
                            </p>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                Bei Punktegleichstand entscheidet am Ende die höhere Anzahl 10 Punkte und anschliessend 8 Punkte-Tipps usw. über die Rangfolge.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Prizes */}
                    <Card className="bg-black/90 shadow-lg border-0 text-white">
                        <CardHeader className="bg-[#00ad43]/20 border-b border-gray-700 py-3">
                            <CardTitle className="flex items-center gap-2 text-base text-white">
                                <Trophy className="w-4 h-4 text-[#00ad43]" />
                                Preise
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 rounded p-2 border border-yellow-500/30">
                                    <span className="font-heading text-sm text-yellow-500 w-6">1.</span>
                                    <p className="text-sm text-white">AGROLA Gutschein <span className="text-gray-400">CHF 400.-</span></p>
                                </div>
                                <div className="flex items-center gap-3 bg-gradient-to-r from-gray-400/20 to-gray-500/10 rounded p-2 border border-gray-400/30">
                                    <span className="font-heading text-sm text-gray-400 w-6">2.</span>
                                    <p className="text-sm text-white">Römerhof Arbon Gutschein <span className="text-gray-400">CHF 200.--</span></p>
                                </div>
                                <div className="flex items-center gap-3 bg-gradient-to-r from-amber-700/20 to-amber-800/10 rounded p-2 border border-amber-700/30">
                                    <span className="font-heading text-sm text-amber-600 w-6">3.</span>
                                    <p className="text-sm text-white">Pizzeria Fontana Gutschein <span className="text-gray-400">CHF 100.--</span></p>
                                </div>
                                <div className="flex items-center gap-3 bg-[#00ad43]/10 rounded p-2 border border-[#00ad43]/30">
                                    <span className="font-heading text-sm text-[#00ad43] w-8">4-10</span>
                                    <p className="text-sm text-white">Ticket SC Brühl Heimspiel</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-gray-700 mt-8 py-4 text-center text-xs text-gray-400 bg-black/50">
                <p>© {new Date().getFullYear()} Kronentipp - SC Brühl St. Gallen</p>
            </footer>

            {/* Rank History Modal */}
            <Dialog open={rankHistoryModal.open} onOpenChange={(open) => setRankHistoryModal({ ...rankHistoryModal, open })}>
                <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white">
                            <LineChart className="w-5 h-5 text-[#00ad43]" />
                            Rangverlauf: {rankHistoryModal.userName}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {loadingHistory ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="spinner" />
                        </div>
                    ) : rankHistory.length === 0 ? (
                        <p className="text-gray-400 text-center py-8">Noch keine ausgewerteten Spiele</p>
                    ) : (
                        <div className="py-4">
                            {/* Simple SVG Chart */}
                            {(() => {
                                const totalParticipants = leaderboard.length || 8;
                                const maxRank = Math.max(totalParticipants, ...rankHistory.map(h => h.rank));
                                // Calculate step size for y-axis labels (show max 10 labels)
                                const labelStep = maxRank <= 10 ? 1 : Math.ceil(maxRank / 10);
                                const yAxisLabels = [];
                                for (let i = 1; i <= maxRank; i += labelStep) {
                                    yAxisLabels.push(i);
                                }
                                if (yAxisLabels[yAxisLabels.length - 1] !== maxRank) {
                                    yAxisLabels.push(maxRank);
                                }
                                
                                const chartHeight = 200;
                                const getY = (rank) => 20 + ((rank - 1) / (maxRank - 1 || 1)) * (chartHeight - 20);
                                
                                return (
                                    <div className="bg-gray-800 rounded-lg p-4">
                                        <svg viewBox="0 0 600 250" className="w-full h-48">
                                            {/* Y-axis labels (ranks) */}
                                            {yAxisLabels.map((rank) => (
                                                <g key={rank}>
                                                    <text
                                                        x="25"
                                                        y={getY(rank) + 4}
                                                        fill="#9ca3af"
                                                        fontSize="11"
                                                        textAnchor="middle"
                                                    >
                                                        {rank}
                                                    </text>
                                                    <line
                                                        x1="40"
                                                        y1={getY(rank)}
                                                        x2="580"
                                                        y2={getY(rank)}
                                                        stroke="#374151"
                                                        strokeDasharray="4"
                                                    />
                                                </g>
                                            ))}
                                            
                                            {/* Chart line */}
                                            {rankHistory.length > 1 && (
                                                <polyline
                                                    fill="none"
                                                    stroke="#00ad43"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    points={rankHistory.map((item, idx) => {
                                                        const x = 60 + (idx * (520 / (rankHistory.length - 1)));
                                                        const y = getY(item.rank);
                                                        return `${x},${y}`;
                                                    }).join(' ')}
                                                />
                                            )}
                                            
                                            {/* Data points */}
                                            {rankHistory.map((item, idx) => {
                                                const x = rankHistory.length > 1 
                                                    ? 60 + (idx * (520 / (rankHistory.length - 1)))
                                                    : 300;
                                                const y = getY(item.rank);
                                                return (
                                                    <g key={idx}>
                                                        <circle
                                                            cx={x}
                                                            cy={y}
                                                            r="6"
                                                            fill="#00ad43"
                                                            stroke="white"
                                                            strokeWidth="2"
                                                        />
                                                        {/* Rank label */}
                                                        <text
                                                            x={x}
                                                            y={y - 12}
                                                            fill="white"
                                                            fontSize="10"
                                                            textAnchor="middle"
                                                            fontWeight="bold"
                                                        >
                                                            {item.rank}
                                                        </text>
                                                    </g>
                                                );
                                            })}
                                            
                                            {/* X-axis labels (match dates) */}
                                            {rankHistory.map((item, idx) => {
                                                const x = rankHistory.length > 1 
                                                    ? 60 + (idx * (520 / (rankHistory.length - 1)))
                                                    : 300;
                                                return (
                                                    <text
                                                        key={idx}
                                                        x={x}
                                                        y={chartHeight + 25}
                                                        fill="#9ca3af"
                                                        fontSize="9"
                                                        textAnchor="middle"
                                                        transform={`rotate(-45, ${x}, ${chartHeight + 25})`}
                                                    >
                                                        {item.match_date ? format(parseISO(item.match_date), 'dd.MM', { locale: de }) : `R${idx + 1}`}
                                                    </text>
                                                );
                                            })}
                                        </svg>
                                        <p className="text-xs text-gray-500 text-center mt-2">
                                            Anzahl Teilnehmer: {totalParticipants} | Rang 1 = oben, Rang {maxRank} = unten
                                        </p>
                                    </div>
                                );
                            })()}
                            
                            {/* Match list */}
                            <div className="mt-4 max-h-40 overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-gray-700">
                                            <TableHead className="text-xs text-gray-400">Spiel</TableHead>
                                            <TableHead className="text-xs text-gray-400">Datum</TableHead>
                                            <TableHead className="text-xs text-gray-400 text-center">Rang</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rankHistory.map((item, idx) => (
                                            <TableRow key={idx} className="border-gray-700">
                                                <TableCell className="text-xs py-1 text-white">{item.match_label}</TableCell>
                                                <TableCell className="text-xs py-1 text-gray-400">
                                                    {format(parseISO(item.match_date), 'dd.MM.yy', { locale: de })}
                                                </TableCell>
                                                <TableCell className="text-center py-1">
                                                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                                                        item.rank === 1 ? 'bg-[#00ad43] text-white' :
                                                        item.rank === 2 ? 'bg-[#00ad43]/70 text-white' :
                                                        item.rank === 3 ? 'bg-[#00ad43]/50 text-white' : 
                                                        'bg-gray-600 text-white'
                                                    }`}>
                                                        {item.rank}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Public Tip Registration Dialog */}
            <Dialog open={showTipDialog} onOpenChange={setShowTipDialog}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl">
                            <Edit className="w-5 h-5 text-[#00ad43]" />
                            Spiele tippen - Kronentipp Anmeldung
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto space-y-6 py-4">
                        {/* Personal Information */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-lg border-b pb-2">Persönliche Angaben</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="tip-name">Vorname / Name / Firma *</Label>
                                    <Input
                                        id="tip-name"
                                        value={tipForm.name_firma}
                                        onChange={(e) => setTipForm(prev => ({ ...prev, name_firma: e.target.value }))}
                                        placeholder="z.B. Max Muster oder Muster AG"
                                        data-testid="tip-name-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="tip-email">Email *</Label>
                                    <Input
                                        id="tip-email"
                                        type="email"
                                        value={tipForm.email}
                                        onChange={(e) => setTipForm(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="ihre@email.ch"
                                        data-testid="tip-email-input"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="tip-address">Adresse, PLZ, Ort *</Label>
                                <Input
                                    id="tip-address"
                                    value={tipForm.address}
                                    onChange={(e) => setTipForm(prev => ({ ...prev, address: e.target.value }))}
                                    placeholder="Musterstrasse 1, 9000 St. Gallen"
                                    data-testid="tip-address-input"
                                />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="tip-player">Ich unterstütze mit meinem Beitrag folgenden Spieler / Trainer</Label>
                                    <Input
                                        id="tip-player"
                                        value={tipForm.supported_player}
                                        onChange={(e) => setTipForm(prev => ({ ...prev, supported_player: e.target.value }))}
                                        placeholder="Optional"
                                        data-testid="tip-player-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="tip-amount">Betrag pro Tor der 1. Mannschaft (min. CHF 2) *</Label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-muted-foreground">CHF</span>
                                        <Input
                                            id="tip-amount"
                                            type="number"
                                            min="2"
                                            step="0.5"
                                            value={tipForm.amount_per_goal}
                                            onChange={(e) => setTipForm(prev => ({ ...prev, amount_per_goal: e.target.value }))}
                                            placeholder="2.00"
                                            className="w-24"
                                            data-testid="tip-amount-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Match Predictions */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-lg border-b pb-2">Ihre Tipps für die Rückrunde</h3>
                            
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-28">Datum</TableHead>
                                            <TableHead>Heimteam</TableHead>
                                            <TableHead>Gastteam</TableHead>
                                            <TableHead className="w-32 text-center">Ihr Tipp</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {allMatches.map(match => (
                                            <TableRow key={match.id}>
                                                <TableCell className="text-sm">
                                                    {format(parseISO(match.match_date), 'dd.MM.yy', { locale: de })}
                                                </TableCell>
                                                <TableCell className="font-medium">{match.home_team}</TableCell>
                                                <TableCell>{match.away_team}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center justify-center gap-1">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max="20"
                                                            className="w-14 h-8 text-center"
                                                            value={tipPredictions[match.id]?.home || ''}
                                                            onChange={(e) => handleTipPredictionChange(match.id, 'home', e.target.value)}
                                                            data-testid={`tip-home-${match.id}`}
                                                        />
                                                        <span className="text-muted-foreground font-bold">:</span>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max="20"
                                                            className="w-14 h-8 text-center"
                                                            value={tipPredictions[match.id]?.away || ''}
                                                            onChange={(e) => handleTipPredictionChange(match.id, 'away', e.target.value)}
                                                            data-testid={`tip-away-${match.id}`}
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            
                            <p className="text-xs text-muted-foreground">
                                * Pflichtfelder
                            </p>
                        </div>
                    </div>
                    
                    <DialogFooter className="border-t pt-4">
                        <Button variant="ghost" onClick={() => setShowTipDialog(false)}>
                            Abbrechen
                        </Button>
                        <Button 
                            className="btn-primary gap-2"
                            onClick={handleSubmitTips}
                            disabled={submittingTips}
                            data-testid="submit-tips-button"
                        >
                            <Send className="w-4 h-4" />
                            {submittingTips ? 'Wird gesendet...' : 'Anmeldung absenden'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Authenticated User Tip Dialog */}
            <Dialog open={showUserTipDialog} onOpenChange={setShowUserTipDialog}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Edit className="w-5 h-5 text-[#00ad43]" />
                            Spiele tippen
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto">
                        {loadingUserMatches ? (
                            <div className="flex items-center justify-center py-12">
                                <span className="w-6 h-6 border-2 border-[#00ad43]/30 border-t-[#00ad43] rounded-full animate-spin" />
                                <span className="ml-3 text-muted-foreground">Spiele werden geladen...</span>
                            </div>
                        ) : userMatches.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                Keine offenen Spiele vorhanden.
                            </div>
                        ) : (
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-24">Datum</TableHead>
                                            <TableHead>Heim</TableHead>
                                            <TableHead>Gast</TableHead>
                                            <TableHead className="w-32 text-center">Tipp</TableHead>
                                            <TableHead className="w-10"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {userMatches.map(match => {
                                            const hasPred = !!userPredictions[match.id];
                                            return (
                                                <TableRow key={match.id}>
                                                    <TableCell className="text-sm text-muted-foreground">
                                                        {format(parseISO(match.match_date), 'dd.MM.yy', { locale: de })}
                                                    </TableCell>
                                                    <TableCell className="font-medium text-sm">{match.home_team}</TableCell>
                                                    <TableCell className="text-sm">{match.away_team}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center justify-center gap-1">
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                max="20"
                                                                className="w-14 h-8 text-center"
                                                                value={userTipInputs[match.id]?.home ?? ''}
                                                                onChange={(e) => handleUserTipChange(match.id, 'home', e.target.value)}
                                                                data-testid={`user-tip-home-${match.id}`}
                                                            />
                                                            <span className="text-muted-foreground font-bold">:</span>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                max="20"
                                                                className="w-14 h-8 text-center"
                                                                value={userTipInputs[match.id]?.away ?? ''}
                                                                onChange={(e) => handleUserTipChange(match.id, 'away', e.target.value)}
                                                                data-testid={`user-tip-away-${match.id}`}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {hasPred && <Check className="w-4 h-4 text-green-500" />}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="border-t pt-4">
                        <Button variant="ghost" onClick={() => setShowUserTipDialog(false)}>
                            Abbrechen
                        </Button>
                        <Button 
                            className="btn-primary gap-2"
                            onClick={handleSaveUserTips}
                            disabled={savingUserTips || userMatches.length === 0}
                            data-testid="save-user-tips-button"
                        >
                            <Save className="w-4 h-4" />
                            {savingUserTips ? 'Wird gespeichert...' : 'Tipps speichern'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Profile Popup */}
            <Dialog open={showProfilePopup} onOpenChange={setShowProfilePopup}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="w-5 h-5 text-[#00ad43]" />
                            Mein Profil
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1 text-sm">
                            <span className="text-muted-foreground">Name</span>
                            <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                        </div>
                        <div className="space-y-1 text-sm">
                            <span className="text-muted-foreground">Email</span>
                            <p className="font-medium">{user?.email}</p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="profile-pseudonym">Pseudonym</Label>
                            <Input
                                id="profile-pseudonym"
                                value={editPseudonym}
                                onChange={(e) => setEditPseudonym(e.target.value)}
                                placeholder="Dein Pseudonym"
                                data-testid="profile-pseudonym-input"
                            />
                            <p className="text-xs text-muted-foreground">Wird in der Rangliste angezeigt</p>
                        </div>
                    </div>
                    <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                        <Button 
                            className="btn-primary w-full gap-2"
                            onClick={handleSaveProfile}
                            disabled={savingProfile}
                            data-testid="save-profile-button"
                        >
                            <Save className="w-4 h-4" />
                            {savingProfile ? 'Speichern...' : 'Speichern'}
                        </Button>
                        <Button 
                            variant="outline" 
                            className="w-full gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => { setShowProfilePopup(false); handleLogout(); }}
                            data-testid="profile-logout-button"
                        >
                            <LogOut className="w-4 h-4" />
                            Abmelden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
